import {
  PROACTIVE_STATUS_TRANSITIONS,
  REQUIRED_PRODUCT_FIELDS,
  STATUS_TRANSITIONS,
} from './constants';
import type {
  ProductSubcategory,
  ProactiveProposalStatus,
  QuotationStatus,
  SubmissionProduct,
  VendorTradeTerms,
  VendorProposalProduct,
} from './types';
import { missingVendorTradeTerms } from './trade-terms';

export interface MarginInput {
  exTaxPrice: number;
  suggestedRetailPrice: number;
  taxRate?: number;
  method?: 'RETAIL_MINUS_COST' | 'RETAIL_MINUS_TAXED_COST';
}

export function calculateTaxIncluded(exTaxPrice: number, taxRate: number): number {
  if (!Number.isFinite(exTaxPrice) || !Number.isFinite(taxRate) || exTaxPrice < 0 || taxRate < 0) {
    return 0;
  }
  return roundMoney(exTaxPrice * (1 + taxRate / 100));
}

export function calculateGrossMarginRate({
  exTaxPrice,
  suggestedRetailPrice,
  taxRate = 5,
  method = 'RETAIL_MINUS_COST',
}: MarginInput): number | null {
  if (
    !Number.isFinite(exTaxPrice) ||
    !Number.isFinite(suggestedRetailPrice) ||
    suggestedRetailPrice <= 0 ||
    exTaxPrice < 0
  ) {
    return null;
  }
  const cost =
    method === 'RETAIL_MINUS_TAXED_COST' ? calculateTaxIncluded(exTaxPrice, taxRate) : exTaxPrice;
  return roundRate((suggestedRetailPrice - cost) / suggestedRetailPrice);
}

export interface GrossMarginResult {
  amount: number | null;
  rate: number | null;
}

export function calculateGrossMargin(input: MarginInput): GrossMarginResult {
  const rate = calculateGrossMarginRate(input);
  if (rate === null || !Number.isFinite(input.suggestedRetailPrice)) return { amount: null, rate };
  const cost =
    input.method === 'RETAIL_MINUS_TAXED_COST'
      ? calculateTaxIncluded(input.exTaxPrice, input.taxRate ?? 5)
      : input.exTaxPrice;
  return { amount: roundMoney(input.suggestedRetailPrice - cost), rate };
}

export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function roundRate(value: number): number {
  return Math.round((value + Number.EPSILON) * 10000) / 10000;
}

export function formatProductCode({
  largeCode,
  mediumCode,
  smallCode,
}: {
  largeCode: string;
  mediumCode: string;
  smallCode: string;
}): string {
  return [largeCode, mediumCode, smallCode]
    .map((value) => value.trim())
    .filter(Boolean)
    .join('-');
}

export function isValidBarcode(value: string): boolean {
  const normalized = value.replace(/[-\s]/g, '');
  if (!/^\d+$/.test(normalized) || ![8, 12, 13, 14].includes(normalized.length)) return false;
  const digits = normalized.split('').map(Number);
  const checkDigit = digits.pop();
  if (checkDigit === undefined) return false;
  let sum = 0;
  for (let index = digits.length - 1, position = 0; index >= 0; index -= 1, position += 1) {
    sum += digits[index] * (position % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10 === checkDigit;
}

export function normalizeBarcodes(values: string[]): string[] {
  return [...new Set(values.map((value) => value.replace(/[-\s]/g, '').trim()).filter(Boolean))];
}

export function findDuplicateValues(values: string[]): string[] {
  const counts = new Map<string, number>();
  values
    .map((value) => value.replace(/[-\s]/g, '').trim())
    .filter(Boolean)
    .forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1));
  return [...counts.entries()].filter(([, count]) => count > 1).map(([value]) => value);
}

export function validateProductBarcodes(
  product: Pick<SubmissionProduct, 'barcodes' | 'outer_barcode' | 'inner_barcode'>,
): string[] {
  const values = [...product.barcodes, product.outer_barcode, product.inner_barcode]
    .map((value) => value.replace(/[-\s]/g, '').trim())
    .filter(Boolean);
  const errors: string[] = [];
  if (product.barcodes.filter(Boolean).length > 6) errors.push('國際條碼最多六組。');
  if (new Set(values).size !== values.length) errors.push('條碼不得重複。');
  values.forEach((value) => {
    if (!isValidBarcode(value)) errors.push(`條碼 ${value} 格式或檢核碼不正確。`);
  });
  return errors;
}

const QUANTIFIED_CONTENT_WARNING =
  '目前內容可能缺少明確量化資訊，請確認是否已填寫實際數量、金額、折扣或比例。';

function selectedYes(enabled: unknown, legacyValue: unknown): boolean {
  if (enabled === true || enabled === 1) return true;
  const value = String(legacyValue ?? '')
    .trim()
    .toUpperCase();
  return value === '有' || value === 'YES' || value === 'TRUE' || value === '1';
}

export function getQuantifiedContentWarnings(
  product: Partial<
    Pick<
      VendorProposalProduct,
      | 'initial_order_offer_enabled'
      | 'initial_order_offer'
      | 'first_order_offer'
      | 'initial_order_offer_content'
      | 'gift_bundle_support_enabled'
      | 'gift_bundle_support'
      | 'gifts'
      | 'gift_bundle_support_content'
      | 'promotion_support_enabled'
      | 'promotion_support'
      | 'promotion_content'
      | 'trial_support_enabled'
      | 'sample_support'
      | 'trial_support_content'
    >
  >,
): string[] {
  const checks = [
    {
      enabled: selectedYes(product.initial_order_offer_enabled, product.initial_order_offer),
      content:
        product.initial_order_offer_content ||
        product.initial_order_offer ||
        product.first_order_offer,
      label: '首批優惠內容',
    },
    {
      enabled: selectedYes(product.gift_bundle_support_enabled, product.gift_bundle_support),
      content: product.gift_bundle_support_content || product.gift_bundle_support || product.gifts,
      label: '贈品及搭贈內容',
    },
    {
      enabled: selectedYes(product.promotion_support_enabled, product.promotion_support),
      content: product.promotion_content,
      label: '促銷內容',
    },
    {
      enabled: selectedYes(product.trial_support_enabled, product.sample_support),
      content: product.trial_support_content,
      label: '試用／體驗支援內容',
    },
  ];
  return checks
    .filter(
      ({ enabled, content }) =>
        enabled && String(content ?? '').trim() && !/\d/.test(String(content)),
    )
    .map(({ label }) => `${label}：${QUANTIFIED_CONTENT_WARNING}`);
}

export function isProductSubcategoryCompatible(
  categoryCode: string,
  subcategory: Pick<ProductSubcategory, 'category_code' | 'is_active'> | null | undefined,
  activeOnly = true,
): boolean {
  return Boolean(
    subcategory &&
    subcategory.category_code === categoryCode.trim() &&
    (!activeOnly || subcategory.is_active === 1),
  );
}

export function validateStatusTransition(from: QuotationStatus, to: QuotationStatus): boolean {
  return STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

export function validateProactiveStatusTransition(
  from: ProactiveProposalStatus,
  to: ProactiveProposalStatus,
): boolean {
  return PROACTIVE_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}

export function resolveProactiveSubmissionStatus(input: {
  waitingVendorApproval: boolean;
  proposalSource?: string | null;
}): ProactiveProposalStatus {
  if (input.waitingVendorApproval) return 'WAITING_VENDOR_APPROVAL';
  return 'WAITING_INITIAL_SCREEN';
}

export interface ProactiveCompletenessInput {
  product: Partial<VendorProposalProduct> | null | undefined;
  tradeTerms?: VendorTradeTerms | null;
  attachments: Array<{ type: string }>;
  channels?: Array<{ channel_code?: string; channel_name?: string }>;
  requiredAttachmentTypes?: string[];
  requiredAttachments?: Array<{ type: string; label: string }>;
  currentSalesChannels?: Array<{ channel_code?: string; channel_name?: string }>;
  expectedSalesChannels?: Array<{ channel_code?: string; channel_name?: string }>;
}

export interface ProactiveCompletenessResult {
  score: number;
  missingItems: string[];
  sections: Record<
    'product' | 'pricing' | 'terms' | 'description' | 'sales' | 'images' | 'attachments',
    number
  >;
}

function present(value: unknown): boolean {
  return typeof value === 'number'
    ? Number.isFinite(value) && value >= 0
    : typeof value === 'string'
      ? value.trim().length > 0
      : Boolean(value);
}

export function calculateProactiveCompleteness(
  input: ProactiveCompletenessInput,
): ProactiveCompletenessResult {
  const product = input.product ?? {};
  const legacyProposal = !(
    'product_lifecycle' in product ||
    'expected_launch_date' in product ||
    'minimum_order_amount' in product ||
    'payment_terms' in product ||
    'initial_order_offer_enabled' in product
  );
  const missingItems: string[] = [];
  const completed = new Map<string, boolean>();
  const requireValue = (key: string, label: string, value: unknown) => {
    const legacyOptional = [
      'product_lifecycle',
      'shelf_life',
      'expected_launch_date',
      'minimum_order_amount',
      'initial_order_offer',
      'gift_bundle_support',
      'promotion_support',
      'trial_support',
    ].includes(key);
    const isComplete =
      legacyProposal && legacyOptional
        ? true
        : typeof value === 'number'
          ? Number.isFinite(value) && value >= 0
          : typeof value === 'boolean'
            ? true
            : typeof value === 'string'
              ? value.trim().length > 0
              : Boolean(value);
    completed.set(key, isComplete);
    if (!isComplete) missingItems.push(label);
    return isComplete;
  };
  const lifecycle = product.product_lifecycle || product.lifecycle_type;
  const launchDate = product.expected_launch_date || product.planned_launch_date;
  const hasExplicitSelection = (value: unknown, legacyValue: unknown) =>
    typeof value === 'boolean' || (typeof value === 'number' && (value === 0 || value === 1))
      ? true
      : present(legacyValue);
  const selectionLabel = (value: unknown, legacyValue: unknown): string => {
    if (typeof value === 'boolean') return value ? '有' : '無';
    if (typeof value === 'number' && (value === 0 || value === 1)) return value === 1 ? '有' : '無';
    return present(legacyValue) ? String(legacyValue).trim() : '';
  };
  const isSelectedYes = (value: unknown, legacyValue: unknown): boolean => {
    const label = selectionLabel(value, legacyValue).toUpperCase();
    return ['有', 'YES', 'TRUE', '1'].includes(label);
  };
  const initialOfferSelection = selectionLabel(
    product.initial_order_offer_enabled,
    product.initial_order_offer ?? product.first_order_offer,
  );
  const giftBundleSelection = selectionLabel(
    product.gift_bundle_support_enabled,
    product.gift_bundle_support ?? product.gifts,
  );
  const promotionSelection = selectionLabel(
    product.promotion_support_enabled,
    product.promotion_support,
  );
  const trialSelection = selectionLabel(product.trial_support_enabled, product.sample_support);
  const initialOfferSet = isSelectedYes(
    product.initial_order_offer_enabled,
    product.initial_order_offer ?? product.first_order_offer,
  );
  const giftBundleSet = isSelectedYes(
    product.gift_bundle_support_enabled,
    product.gift_bundle_support ?? product.gifts,
  );
  if (hasExplicitSelection(product.promotion_support_enabled, product.promotion_support))
    product.promotion_support = product.promotion_support || '已選擇';
  if (hasExplicitSelection(product.trial_support_enabled, product.sample_support))
    product.sample_support = product.sample_support || '已選擇';
  const sections = {
    product: 0,
    pricing: 0,
    terms: 0,
    description: 0,
    sales: 0,
    images: 0,
    attachments: 0,
  };
  const productFields: Array<[string, string, unknown]> = [
    ['name_zh', '商品中文名稱', product.name_zh],
    ['brand', '品牌', product.brand],
    ['product_category_code', '商品分類', product.product_category_code],
    ['specification', '商品規格', product.specification],
    ['unit', '銷售單位', product.unit],
    ['case_pack', '箱入數', product.case_pack],
    ['product_lifecycle', '商品生命週期', lifecycle],
    ['shelf_life', '保存期限', product.shelf_life],
    ['expected_launch_date', '預計上市日期', launchDate],
  ];
  productFields.forEach(([key, label, value]) => requireValue(key, label, value));
  sections.product = Math.round(
    (productFields.filter(([key]) => completed.get(key)).length / productFields.length) * 100,
  );

  const barcodeValues = [
    ...(product.barcodes ?? []),
    product.outer_barcode,
    product.inner_barcode,
  ].filter((value) => present(value));
  const noBarcode = Number(product.no_international_barcode) === 1;
  if (barcodeValues.length && noBarcode)
    missingItems.push('已輸入國際條碼，請先移除條碼後再選擇無國際條碼。');
  if (!barcodeValues.length && !noBarcode)
    missingItems.push('請至少填寫一組國際條碼；若商品依規範無國際條碼，請選擇本商品無國際條碼。');
  if (noBarcode) {
    requireValue(
      'no_international_barcode_reason',
      '無國際條碼原因',
      product.no_international_barcode_reason,
    );
    if (product.no_international_barcode_reason === 'OTHER')
      requireValue(
        'no_international_barcode_other_reason',
        '其他無國際條碼原因說明',
        product.no_international_barcode_other_reason,
      );
  }

  const pricingFields: Array<[string, string, unknown]> = [
    ['ex_tax_price', '未稅進價', product.ex_tax_price],
    ['tax_included_price', '含稅進價', product.tax_included_price],
    ['tax_rate', '稅率', product.tax_rate],
    ['suggested_retail_price', '建議售價', product.suggested_retail_price],
    ['minimum_order_quantity', '最低訂購量', product.minimum_order_quantity],
    ['minimum_delivery_unit', '最小配送單位', product.minimum_delivery_unit],
    ['minimum_order_amount', '最低訂購金額', product.minimum_order_amount],
  ];
  pricingFields.forEach(([key, label, value]) => requireValue(key, label, value));
  const tradeTermMissing = missingVendorTradeTerms(input.tradeTerms);
  tradeTermMissing.forEach((label) => missingItems.push(`廠商交易條件：${label}`));
  requireValue('initial_order_offer', '首批優惠', initialOfferSelection);
  if (initialOfferSet)
    requireValue(
      'initial_order_offer_content',
      '首批優惠內容',
      product.initial_order_offer_content ||
        product.initial_order_offer ||
        product.first_order_offer,
    );
  else completed.set('initial_order_offer_content', true);
  requireValue('gift_bundle_support', '贈品及搭贈', giftBundleSelection);
  if (giftBundleSet)
    requireValue(
      'gift_bundle_support_content',
      '贈品及搭贈內容',
      product.gift_bundle_support_content || product.gift_bundle_support || product.gifts,
    );
  else completed.set('gift_bundle_support_content', true);
  const promotionEnabled = isSelectedYes(
    product.promotion_support_enabled,
    product.promotion_support,
  );
  requireValue('promotion_support', '促銷支援', promotionSelection);
  if (promotionEnabled) {
    requireValue('promotion_content', '促銷內容', product.promotion_content);
    requireValue('promotion_start_date', '促銷開始日期', product.promotion_start_date);
    requireValue('promotion_end_date', '促銷結束日期', product.promotion_end_date);
    if (
      product.promotion_start_date &&
      product.promotion_end_date &&
      product.promotion_end_date < product.promotion_start_date
    )
      missingItems.push('促銷結束日期不得早於促銷開始日期。');
  }
  const trialEnabled = isSelectedYes(product.trial_support_enabled, product.sample_support);
  requireValue('trial_support', '試用支援', trialSelection);
  if (trialEnabled)
    requireValue('trial_support_content', '試用支援內容', product.trial_support_content);
  else completed.set('trial_support_content', true);
  const pricingKeys = pricingFields
    .map(([key]) => key)
    .concat([
      'initial_order_offer',
      'initial_order_offer_content',
      'gift_bundle_support',
      'gift_bundle_support_content',
      'promotion_support',
      'trial_support',
      'trial_support_content',
      'trade_terms',
    ]);
  completed.set('trade_terms', tradeTermMissing.length === 0);
  sections.pricing = Math.round(
    (pricingKeys.filter((key) => completed.get(key)).length / pricingKeys.length) * 100,
  );
  const termKeys = [
    'minimum_delivery_unit',
    'trade_terms',
    'initial_order_offer',
    'initial_order_offer_content',
    'gift_bundle_support',
    'gift_bundle_support_content',
    'promotion_support',
    'trial_support',
    'trial_support_content',
  ];
  sections.terms = Math.round(
    (termKeys.filter((key) => completed.get(key)).length / termKeys.length) * 100,
  );

  const descriptionFields: Array<[string, string, unknown]> = [
    ['key_features', '商品主要特色', product.key_features || product.product_features],
    ['target_customers', '主要消費客群', product.target_customers],
    ['competitor_difference', '與競品差異', product.competitor_difference],
    ['suggested_price_rationale', '建議售價理由', product.suggested_price_rationale],
  ];
  descriptionFields.forEach(([key, label, value]) => requireValue(key, label, value));
  sections.description = Math.round(
    (descriptionFields.filter(([key]) => completed.get(key)).length / descriptionFields.length) *
      100,
  );

  const expectedSalesChannels = input.expectedSalesChannels ?? input.channels ?? [];
  if (
    !expectedSalesChannels.some(
      (channel) => present(channel.channel_code) || present(channel.channel_name),
    )
  )
    missingItems.push('請選擇預計銷售通路。');
  const salesFields: Array<[string, string, unknown]> = [
    ['market_positioning', '商品市場定位', product.market_positioning],
    ['expected_sales_channels', '預計銷售通路', expectedSalesChannels.length ? '已選擇' : ''],
    ['launch_plan', '上市推廣計畫', product.launch_plan],
  ];
  salesFields.forEach(([key, label, value]) => requireValue(key, label, value));
  sections.sales = Math.round(
    (salesFields.filter(([key]) => completed.get(key)).length / salesFields.length) * 100,
  );

  const attachmentTypes = new Set(input.attachments.map((attachment) => attachment.type));
  const imageTypes = ['PRODUCT_FRONT', 'PRODUCT_BACK'];
  const missingImages = imageTypes.filter((type) => !attachmentTypes.has(type));
  missingImages.forEach((type) =>
    missingItems.push(type === 'PRODUCT_FRONT' ? '商品正面圖片' : '商品背面圖片'),
  );
  sections.images = Math.round(
    ((imageTypes.length - missingImages.length) / imageTypes.length) * 100,
  );
  const requiredAttachments = input.requiredAttachments?.length
    ? input.requiredAttachments
    : [...new Set(['QUOTATION', ...(input.requiredAttachmentTypes ?? [])])].map((type) => ({
        type,
        label: type,
      }));
  const effectiveRequiredAttachments = requiredAttachments.filter(
    (item) => !(item.type === 'BARCODE_IMAGE' && noBarcode),
  );
  const missingAttachments = effectiveRequiredAttachments.filter(
    (item) => !attachmentTypes.has(item.type),
  );
  missingAttachments.forEach((item) => missingItems.push(`必要附件：${item.label}`));
  sections.attachments = effectiveRequiredAttachments.length
    ? Math.round(
        ((effectiveRequiredAttachments.length - missingAttachments.length) /
          effectiveRequiredAttachments.length) *
          100,
      )
    : attachmentTypes.size > 0
      ? 100
      : 0;
  const weighted =
    sections.product * 0.2 +
    sections.pricing * 0.2 +
    sections.terms * 0.15 +
    sections.description * 0.15 +
    sections.sales * 0.1 +
    sections.images * 0.1 +
    sections.attachments * 0.1;
  return {
    score: Math.round(weighted * 100) / 100,
    missingItems: [...new Set(missingItems)],
    sections,
  };
}

export function getMissingProductFields(product: Partial<SubmissionProduct>): string[] {
  const labels: Partial<Record<keyof SubmissionProduct, string>> = {
    name_zh: '商品中文名稱',
    brand: '品牌',
    product_category_code: '商品分類',
    specification: '規格',
    unit: '單位',
    origin: '產地',
    case_pack: '箱入數',
    ex_tax_price: '未稅進價',
    tax_included_price: '含稅進價',
    suggested_retail_price: '建議售價',
  };
  Object.assign(labels, {
    shelf_life: '保存期限',
    tax_rate: '稅率',
    minimum_order_quantity: '最低訂購量',
    minimum_delivery_unit: '最小配送單位',
    minimum_order_amount: '最低訂購金額',
  });
  return REQUIRED_PRODUCT_FIELDS.filter((field) => {
    const value = product[field];
    return (
      value === undefined ||
      value === null ||
      value === '' ||
      (typeof value === 'number' && value < 0)
    );
  }).map((field) => labels[field] ?? String(field));
}

export function getMissingProductMarketingFields(
  product: Partial<SubmissionProduct> | Partial<VendorProposalProduct>,
): string[] {
  const values = product as Record<string, unknown>;
  const selected = (enabled: unknown, legacy: unknown): boolean | null => {
    if (enabled === true || Number(enabled) === 1 || enabled === 'YES' || enabled === '有')
      return true;
    if (enabled === 'NO' || enabled === '無') return false;
    if (enabled === false || Number(enabled) === 0) {
      return typeof legacy === 'string' && legacy.trim() ? false : null;
    }
    if (typeof legacy === 'string' && legacy.trim())
      return legacy.trim() === '有' || legacy.trim() === 'YES';
    return null;
  };
  const missing: string[] = [];
  const initial = selected(
    values.initial_order_offer_enabled,
    values.initial_order_offer ?? values.first_order_offer,
  );
  const gifts = selected(
    values.gift_bundle_support_enabled,
    values.gift_bundle_support ?? values.gifts,
  );
  const promotion = selected(
    values.promotion_support_enabled,
    values.promotion_support ?? values.promotion_content,
  );
  const trial = selected(
    values.trial_support_enabled,
    values.sample_support ?? values.trial_support_content,
  );
  if (initial === null) missing.push('首批優惠');
  if (
    initial === true &&
    !String(
      values.initial_order_offer_content ??
        values.initial_order_offer ??
        values.first_order_offer ??
        '',
    ).trim()
  )
    missing.push('首批優惠內容');
  if (gifts === null) missing.push('贈品及搭贈');
  if (
    gifts === true &&
    !String(
      values.gift_bundle_support_content ?? values.gift_bundle_support ?? values.gifts ?? '',
    ).trim()
  )
    missing.push('贈品及搭贈內容');
  if (promotion === null) missing.push('促銷支援');
  if (promotion === true) {
    if (!String(values.promotion_content ?? '').trim()) missing.push('促銷內容');
    if (!String(values.promotion_start_date ?? '').trim()) missing.push('促銷開始日期');
    if (!String(values.promotion_end_date ?? '').trim()) missing.push('促銷結束日期');
    if (
      values.promotion_start_date &&
      values.promotion_end_date &&
      String(values.promotion_end_date) < String(values.promotion_start_date)
    )
      missing.push('促銷結束日期不得早於開始日期');
  }
  if (trial === null) missing.push('試用支援');
  if (trial === true && !String(values.trial_support_content ?? values.sample_support ?? '').trim())
    missing.push('試用支援內容');
  return missing;
}

export function getProductMarketingWarnings(
  product: Partial<SubmissionProduct> | Partial<VendorProposalProduct>,
): string[] {
  const values = product as Record<string, unknown>;
  const warnings: string[] = [];
  const check = (enabled: unknown, content: unknown, label: string) => {
    if (
      (enabled === true || Number(enabled) === 1 || enabled === 'YES' || enabled === '有') &&
      !/\d/.test(String(content ?? ''))
    )
      warnings.push(`${label}建議包含數量、金額、折扣或期限等量化資訊。`);
  };
  check(
    values.initial_order_offer_enabled,
    values.initial_order_offer_content ?? values.initial_order_offer,
    '首批優惠',
  );
  check(
    values.gift_bundle_support_enabled,
    values.gift_bundle_support_content ?? values.gift_bundle_support,
    '贈品及搭贈',
  );
  check(values.promotion_support_enabled, values.promotion_content, '促銷支援');
  check(
    values.trial_support_enabled,
    values.trial_support_content ?? values.sample_support,
    '試用支援',
  );
  return warnings;
}

export function calculateAverageScore(scores: number[]): number | null {
  if (!scores.length) return null;
  const valid = scores.filter((score) => score >= 1 && score <= 5);
  if (!valid.length) return null;
  return Math.round((valid.reduce((sum, score) => sum + score, 0) / valid.length) * 100) / 100;
}

export function formatTaipeiDate(
  value: string | Date | null | undefined,
  withTime = false,
): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('zh-TW', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  })
    .format(date)
    .replaceAll('/', '-');
}
